import {
  IInstantEnergyDispatchSecurity,
  ISellerUtilization,
  IEfficiencyTrend,
  IAlarmCountStatistics,
} from '@models/home';
import { createSlice } from '@reduxjs/toolkit';

interface IBasicInfo {
  parkingSpaceCount: number | undefined;
  stationCount: number | undefined;
  pileCount: number | undefined;
  gunCount: number | undefined;
  members: number | undefined;
}

type InstantSellerUtilization = Omit<
  ISellerUtilization,
  | 'maximumMinutesMap'
  | 'normalOperationMinutesMap'
  | 'utilizationMap'
  | 'timeList'
> & {
  maximumMinute: number | undefined;
  normalOperationMinute: number | undefined;
  utilization: number | undefined;
};

interface IInstantChargingEquipmentStability {
  sellerUtilization: InstantSellerUtilization;
  efficiencyTrend: Omit<
    IEfficiencyTrend,
    'timeList' | 'efficiency' | 'pileOutputPowers' | 'pileMaxmunPowers'
  > & {
    efficiency: number | undefined;
    pileOutputPower: number | undefined;
    pileMaxmunPower: number | undefined;
  };
  statusRate: {
    totalAvailableGunCount: undefined;
    totalChargingGunCount: undefined;
    totalFaultedGunCount: undefined;
    totalHoggingGunCount: undefined;
    totalReservedGunCount: undefined;
    totalGunCount: undefined;
    totalOfflineGunCount: undefined;
    totalUnavailableGunCount: undefined;
  };
  alarmCountStatistics: Omit<
    IAlarmCountStatistics,
    | 'timeList'
    | 'highSeverityCountList'
    | 'mediumSeverityCountList'
    | 'lowSeverityCountList'
    | 'totalGunCountList'
  > & {
    highSeverityCount: number | undefined;
    mediumSeverityCount: number | undefined;
    lowSeverityCount: number | undefined;
    totalGunCount: number | undefined;
  };
}

interface IInstantFinancialProfitabilityEfficiency {
  chargingPerformance: {
    duration: number | undefined;
    normalOperationMinutes: number | undefined;
  };
  incomeStatistics: {
    money: number | undefined;
    serviceMoney: number | undefined;
  } | null;
  chargingTurnoverRate: {
    totalBillCount: number | undefined;
    totalGunCount: number | undefined;
  };
}

interface IInitianState {
  BasicInfo: IBasicInfo;
  EnergyDispatchSecurity: IInstantEnergyDispatchSecurity;
  ChargingEquipmentStability: IInstantChargingEquipmentStability;
  FinancialProfitabilityEfficiency: IInstantFinancialProfitabilityEfficiency;
}

const initialState: IInitianState = {
  BasicInfo: {
    parkingSpaceCount: undefined,
    stationCount: undefined,
    pileCount: undefined,
    gunCount: undefined,
    members: undefined,
  },
  EnergyDispatchSecurity: {
    instantSellerPower: {
      chargingPower: undefined,
      contractedEnergy: undefined,
    },
    instantSellerCurrent: {
      chargingCurrent: undefined,
      currentLimit: undefined,
    },
    instantSellerPile: {
      totalPileCount: undefined,
      chargingPileCount: undefined,
    },
  },
  ChargingEquipmentStability: {
    sellerUtilization: {
      maximumMinute: undefined,
      normalOperationMinute: undefined,
      utilization: undefined,
    },
    efficiencyTrend: {
      efficiency: undefined,
      pileOutputPower: undefined,
      pileMaxmunPower: undefined,
    },
    statusRate: {
      totalAvailableGunCount: undefined,
      totalChargingGunCount: undefined,
      totalFaultedGunCount: undefined,
      totalHoggingGunCount: undefined,
      totalReservedGunCount: undefined,
      totalGunCount: undefined,
      totalOfflineGunCount: undefined,
      totalUnavailableGunCount: undefined,
    },
    alarmCountStatistics: {
      highSeverityCount: undefined,
      mediumSeverityCount: undefined,
      lowSeverityCount: undefined,
      totalGunCount: undefined,
    },
  },
  FinancialProfitabilityEfficiency: {
    chargingPerformance: {
      duration: undefined,
      normalOperationMinutes: undefined,
    },
    incomeStatistics: {
      money: undefined,
      serviceMoney: undefined,
    },
    chargingTurnoverRate: {
      totalBillCount: undefined,
      totalGunCount: undefined,
    },
  },
};

const dashboardRealTimeValueSlice = createSlice({
  name: 'dashboardRealTimeValue',
  initialState,
  reducers: {
    setBasicInfo: (state, action) => {
      state.BasicInfo = action.payload;
    },
    setEnergyDispatchSecurity: (state, action) => {
      state.EnergyDispatchSecurity = action.payload;
    },
    setChargingEquipmentStability: (state, action) => {
      state.ChargingEquipmentStability = action.payload;
    },
    setFinancialProfitabilityEfficiency: (state, action) => {
      state.FinancialProfitabilityEfficiency = action.payload;
    },
  },
});

export const {
  setBasicInfo,
  setEnergyDispatchSecurity,
  setChargingEquipmentStability,
  setFinancialProfitabilityEfficiency,
} = dashboardRealTimeValueSlice.actions;

export default dashboardRealTimeValueSlice.reducer;
